import { useInfiniteQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useMemo } from 'react';
import { tpoGenUrls } from 'config/urls';
import { tpoGenKeys } from '../../../../utils/tpoGenQueryKeys';

const PAGE_LIMIT = 50;

export interface ImportableEventRow {
    event_id: string;
    event_name: string;
    event_start_date: string;
    event_end_date: string;
    promo_id: string;
    promo_name: string;
    attributes: Record<string, string | null>;
}

interface FetchImportableEventsResponse {
    source_plan_id: string;
    source_plan_name: string;
    target_plan_id: string | null;
    target_plan_year: number | null;
    events: ImportableEventRow[];
    total_count: number;
}

const fetchImportableEvents = async (
    sourcePlanId: string,
    targetPlanId: string | null,
    searchString: string,
    offset: number,
    signal?: AbortSignal,
): Promise<FetchImportableEventsResponse> => {
    const token = localStorage.getItem('accessToken');
    const response = await axios(tpoGenUrls.fetchImportableEvents, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        signal,
        data: {
            source_plan_id: sourcePlanId,
            // target_plan_id: targetPlanId || undefined,
            search_string: searchString || null,
            limit: PAGE_LIMIT,
            offset,
        },
    });
    return response.data;
};

const useImportableEvents = ({
    sourcePlanId,
    targetPlanId,
    searchString,
}: {
    sourcePlanId: string | null;
    targetPlanId: string | null;
    searchString: string;
}) => {
    const { data, isPending, isFetching, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteQuery({
        queryKey: tpoGenKeys.importableEvents(sourcePlanId, targetPlanId, searchString),
        queryFn: ({ signal, pageParam }) =>
            fetchImportableEvents(sourcePlanId as string, targetPlanId, searchString, pageParam, signal),
        initialPageParam: 0,
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
            const nextOffset = lastPageParam + PAGE_LIMIT;
            return nextOffset < lastPage.total_count ? nextOffset : undefined;
        },
        enabled: !!sourcePlanId,
    });

    const events = useMemo<ImportableEventRow[]>(() => {
        if (!data?.pages?.length) return [];
        return data.pages.flatMap((page) => page.events);
    }, [data]);

    const totalCount = data?.pages?.[data.pages.length - 1]?.total_count ?? 0;
    const sourcePlanName = data?.pages?.[0]?.source_plan_name ?? '';
    const targetPlanYear = data?.pages?.[0]?.target_plan_year ?? null;

    return {
        events,
        totalCount,
        sourcePlanName,
        targetPlanYear,
        isPending: !!sourcePlanId && isPending,
        isFetching,
        isFetchingNextPage,
        hasNextPage: hasNextPage ?? false,
        fetchNextPage,
    };
};

export default useImportableEvents;
